import React from 'react';
import {
  TextField,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Autocomplete,
  Box,
  Typography,
  Chip,
  Alert,
  AlertTitle,
} from '@mui/material';
import ErrorIcon from '@mui/icons-material/Error';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useQuery } from '@tanstack/react-query';
import type { BeaconAssignment } from '../../api/config';
import { productsApi } from '../../api/services';
import {
  parseAndValidateTags,
  getValidTags,
  ParsedTag,
} from '../../utils/validation';
import { useDebounce } from '../../hooks/useDebounce';
import { FormDialog } from '../FormDialog';

export interface BeaconFormSubmitData {
  beaconIds: string[];
  sku: string;
  status: string;
}

interface BeaconAssignmentFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: BeaconFormSubmitData) => void;
  initialData?: BeaconAssignment;
  existingBeaconIds?: string[];
  error?: string | null;
  isPending?: boolean;
}

const EMPTY_ARRAY: string[] = [];

export const BeaconAssignmentForm: React.FC<BeaconAssignmentFormProps> = ({
  open,
  onClose,
  onSubmit,
  initialData,
  existingBeaconIds = EMPTY_ARRAY,
  error,
  isPending,
}) => {
  const isEdit = !!initialData;

  const [sku, setSku] = React.useState('');
  const [status, setStatus] = React.useState<string>('active');
  const [inputText, setInputText] = React.useState('');
  const [parsedTags, setParsedTags] = React.useState<ParsedTag[]>([]);

  const debouncedInput = useDebounce(inputText, 300);

  // Fetch products for SKU dropdown
  const { data: products = [] } = useQuery({
    queryKey: ['products'],
    queryFn: productsApi.getAll,
  });

  // Reset form state when dialog opens or initialData changes
  React.useEffect(() => {
    if (!open) return;
    if (initialData) {
      setSku(initialData.sku);
      setStatus(initialData.status);
      setInputText('');
      setParsedTags([]);
    } else {
      setSku('');
      setStatus('active');
      setInputText('');
      setParsedTags([]);
    }
  }, [initialData, open]);

  // Parse and validate input tags (create mode only)
  React.useEffect(() => {
    if (isEdit) return;

    if (!debouncedInput.trim()) {
      setParsedTags([]);
      return;
    }

    const parsed = parseAndValidateTags(debouncedInput, existingBeaconIds);
    setParsedTags(parsed);
  }, [debouncedInput, existingBeaconIds, isEdit]);

  const validTags = getValidTags(parsedTags);
  const invalidTags = parsedTags.filter(t => !t.isValid || t.isDuplicate);

  const handleSelectChange = (e: SelectChangeEvent) => {
    setStatus(e.target.value);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (isEdit) {
      onSubmit({
        beaconIds: [initialData!.beacon_id],
        sku,
        status,
      });
    } else {
      if (validTags.length === 0) return;
      onSubmit({
        beaconIds: validTags,
        sku,
        status,
      });
    }
  };

  const canSubmit = isEdit
    ? !!sku
    : validTags.length > 0 && !!sku;

  return (
    <FormDialog
      open={open}
      onClose={onClose}
      onSubmit={handleSubmit}
      title={isEdit ? 'Edit Tag Assignment' : 'New Tag Assignment'}
      submitLabel={isEdit ? 'Save' : `Create${validTags.length > 0 ? ` (${validTags.length})` : ''}`}
      error={error}
      isPending={isPending}
      submitDisabled={!canSubmit}
    >
      <Stack spacing={2} sx={{ mt: 1 }}>
        {isEdit ? (
          <Box>
            <Typography variant="caption" color="text.secondary">
              Tag ID
            </Typography>
            <Chip
              label={initialData!.beacon_id}
              color="info"
              size="small"
              sx={{ display: 'flex', justifyContent: 'center', mt: 0.5 }}
            />
          </Box>
        ) : (
          <>
            <TextField
              label="Tag IDs"
              multiline
              rows={4}
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              fullWidth
              placeholder={'Scan or paste tag IDs (one per line)\ne.g. BC5729047458\nor MAC:BC5729047458,SERIAL:32046;'}
              helperText={`${validTags.length} valid tag${validTags.length !== 1 ? 's' : ''} ready to assign`}
            />

            {validTags.length > 0 && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {parsedTags
                  .filter(t => t.isValid && !t.isDuplicate)
                  .map((tag, index) => (
                    <Chip
                      key={`valid-${tag.formatted}-${index}`}
                      label={tag.formatted}
                      size="small"
                      color="success"
                      variant="outlined"
                      icon={<CheckCircleIcon />}
                    />
                  ))}
              </Box>
            )}

            {invalidTags.length > 0 && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {invalidTags.map((tag, index) => (
                  <Chip
                    key={`invalid-${tag.original}-${index}`}
                    label={`${tag.formatted || tag.original} — ${tag.error || 'Invalid'}`}
                    size="small"
                    color="error"
                    variant="outlined"
                    icon={<ErrorIcon />}
                  />
                ))}
              </Box>
            )}
          </>
        )}

        <Autocomplete
          options={products}
          value={products.find(p => p.sku === sku) ?? null}
          onChange={(_e, product) => {
            setSku(product?.sku ?? '');
          }}
          getOptionLabel={(option) => `${option.sku} (${option.name})`}
          isOptionEqualToValue={(option, value) => option.sku === value.sku}
          renderInput={(params) => (
            <TextField {...params} label="SKU" required />
          )}
          fullWidth
        />

        {isEdit && !!sku && initialData!.sku !== sku && (
          <Alert severity="warning">
            <AlertTitle>Correcting a mis-assignment</AlertTitle>
            Changing the product rewrites this tag's <strong>entire history</strong>{' '}
            (inventory state, events, and assignment log) from{' '}
            <strong>{initialData!.sku}</strong> to <strong>{sku}</strong>. Only do
            this when the original product was assigned by mistake. This is{' '}
            <strong>not</strong> for reusing a tag on a different product — retire
            this tag and assign a new one instead.
          </Alert>
        )}

        <FormControl fullWidth>
          <InputLabel id="status-label">Status</InputLabel>
          <Select
            labelId="status-label"
            id="status"
            name="status"
            value={status}
            label="Status"
            onChange={handleSelectChange}
          >
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="inactive">Inactive</MenuItem>
          </Select>
        </FormControl>
      </Stack>
    </FormDialog>
  );
};
